import { Visibility } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { headerStyle } from "../question.style";
import UploadIcon from "@mui/icons-material/Upload";
import { ChangeEvent } from "react";
import { toastMessage } from "../../../utils/toast";
import { importTranslations } from "../../../Redux/actions/questionTranslationAction";
import BackButton from "../../Common/UI/BackButton";

const LoaderStyle: SxProps = {
  mr: "50px",
};
const LinearProgressStyle: SxProps = {
  width: "200px",
  "& .MuiLinearProgress-root": {
    height: "10px",
    borderRadius: 2.5,
    backgroundColor: "#E5E7EB",
  },
};

const QBHeader = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { loading, formSubmitting, questionName, percentage, editable } =
    useAppSelector((state) => state.questionTranslation);
  const lang = useAppSelector((state) => state.questionTranslation.lang);
  const languages = useAppSelector((state) => state.study.languages);
  const availableLanguages = languages?.filter(
    (lang) => lang.languageCode !== "en"
  );

  const handleBack = () => {
    navigate(-1);
  };

  const handlePreview = () => {
    navigate(`../preview?lang=${lang}`, { relative: "path" });
  };
  const handleTranslation = (lang: string) => {
    navigate(`../edit-translation?lang=${lang}`, { relative: "path" });
  };

  const uploadCSV = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        toastMessage("warning", "File size must smaller than 5MB!");
        return false;
      }
      dispatch(importTranslations(file, lang));
    }
  };

  return (
    <Box sx={headerStyle}>
      <BackButton onClick={handleBack} sx={{ mr: 1 }} />

      <Typography
        variant="h2"
        color="text.primary"
        sx={{
          flex: 1,
          minWidth: "0px",
        }}
        noWrap
      >
        {questionName || "Form Builder"}
      </Typography>

      <span style={{ marginLeft: "auto" }} />

      {!loading ? (
        formSubmitting ? (
          <CircularProgress size={25} sx={LoaderStyle} />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                ...LinearProgressStyle,
                "& .MuiLinearProgress-bar1Determinate": {
                  borderRadius: 2.5,
                  backgroundColor: (theme) =>
                    percentage === 100 ? "#70AE71" : theme.palette.primary.main,
                },
              }}
            >
              <Typography variant="body1" color="text.primary" mb={1}>
                Completion Progress
              </Typography>
              <Tooltip title={`Progress: ${percentage.toFixed(2)}%`}>
                <LinearProgress variant="determinate" value={percentage} />
              </Tooltip>
            </Box>

            <FormControl sx={{ width: "120px" }}>
              <InputLabel id="language-label">Language</InputLabel>
              <Select
                value={lang}
                onChange={(e) => {
                  handleTranslation(e.target.value);
                }}
                id="lang"
                label="Language"
                labelId="language-label"
                sx={{ height: "40px" }}
              >
                {availableLanguages.length > 0 ? (
                  availableLanguages.map((lang) => (
                    <MenuItem key={lang.languageCode} value={lang.languageCode}>
                      {lang.languageLabel}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No languages available</MenuItem>
                )}
              </Select>
            </FormControl>

            {editable && (
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mr: 2 }}
              >
                <span>Import Questions</span>
                <input
                  hidden
                  accept="text/csv"
                  type="file"
                  onChange={uploadCSV}
                />
              </Button>
            )}

            <Button
              onClick={handlePreview}
              variant="outlined"
              sx={{ mr: 2 }}
              startIcon={<Visibility />}
            >
              Preview
            </Button>
          </Box>
        )
      ) : null}
    </Box>
  );
};

export default QBHeader;
